import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';

import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Plus, BarChart2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';

interface EmptyHookTrackStateProps {
  onCreateNew: () => void;
}

export function EmptyHookTrackState({ onCreateNew }: EmptyHookTrackStateProps) {
  useLanguage();
  const [showHowItWorks, setShowHowItWorks] = useState(false);

  return (
    <div className="flex flex-col items-center justify-center min-h-[calc(100vh-200px)] text-center px-4">
      <div className="mb-8 flex justify-center">
        <div className="rounded-full bg-blue-100 p-6 animate-pulse-soft">
          <div className="w-16 h-16 rounded-full bg-blue-500 flex items-center justify-center">
            <BarChart2 size={32} className="text-white" />
          </div>
        </div>
      </div>
      
      <h2 className="text-lg sm:text-xl md:text-2xl font-semibold mb-3 max-w-4xl mx-auto px-2">
        {translateInterfaceText("Teste diferentes introduções e descubra a que converte mais")}</h2>
      <p className="text-sm md:text-base text-muted-foreground mb-8 max-w-md mx-auto leading-relaxed">
        {translateInterfaceText("Compare introduções, distribua o tráfego automaticamente e otimize sua conversão com dados reais")}</p>
      
      <div className="flex flex-col sm:flex-row gap-4">
        <Button onClick={onCreateNew} className="flex items-center justify-center gap-2 min-h-[44px] px-6">
          <Plus className="h-4 w-4" />
          {translateInterfaceText("Criar Teste de Introduções")}</Button>
        
        <Button 
          variant="outline" 
          onClick={() => setShowHowItWorks(true)}
          className="px-6 min-h-[44px]"
        >
          {translateInterfaceText("Como funciona?")}</Button>
      </div>

      <Dialog open={showHowItWorks} onOpenChange={setShowHowItWorks}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{translateInterfaceText("Como funcionam os testes de introduções")}</DialogTitle>
            <DialogDescription className="pt-4">
              <div className="space-y-4">
                <div className="border-l-4 border-blue-500 pl-4 py-2">
                  <h3 className="font-medium mb-1">{translateInterfaceText("1. Crie um teste")}</h3>
                  <p className="text-sm text-gray-600">
                    {translateInterfaceText("Selecione diferentes introduções (hooks) para comparar o desempenho em tempo real.")}</p>
                </div>
                
                <div className="border-l-4 border-blue-500 pl-4 py-2">
                  <h3 className="font-medium mb-1">{translateInterfaceText("2. Distribuição automática")}</h3>
                  <p className="text-sm text-gray-600">
                    {translateInterfaceText("O sistema distribuirá o tráfego entre as introduções selecionadas, garantindo uma amostra estatística válida.")}</p>
                </div>
                
                <div className="border-l-4 border-blue-500 pl-4 py-2">
                  <h3 className="font-medium mb-1">{translateInterfaceText("3. Análise de métricas")}</h3>
                  <p className="text-sm text-gray-600">
                    {translateInterfaceText("Acompanhe visualizações, engajamento, retenção e outras métricas importantes para cada introdução.")}</p>
                </div>
                
                <div className="border-l-4 border-blue-500 pl-4 py-2">
                  <h3 className="font-medium mb-1">{translateInterfaceText("4. Identifique o vencedor")}</h3>
                  <p className="text-sm text-gray-600">
                    {translateInterfaceText("Com base nos dados reais, descubra qual introdução tem melhor performance e finalize o teste.")}</p>
                </div>
              </div>
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </div>
  );
}
